import type { Release } from "../api/releases";

export const OS_LIST = ["win64", "mac", "android", "ios", "linux"] as const;

export interface Installer {
  external: boolean;
  fileId: number | null;
  fileName: string | null;
  link: string;
}

export interface SoftwareProduct {
  id: string;
  name: string;
  iconUrl: string;
  downloadPage: string | null;
  installers: Record<string, Installer>;
}

const MOBILE_ANDROID_LINK =
  "https://play.google.com/store/apps/details?id=com.nextgis.mobile";
const COLLECTOR_ANDROID_LINK =
  "https://play.google.com/store/apps/details?id=com.nextgis.collector";
const TRACKER_ANDROID_LINK =
  "https://play.google.com/store/apps/details?id=com.nextgis.tracker";
const VANILLA_QGIS_DOWNLOAD_PAGE = "https://qgis.org/download/";
const NEXTGIS_QGIS_LINUX_LINK =
  "https://docs.nextgis.ru/docs_ngqgis/source/install.html#install-linux";

const base = import.meta.env.BASE_URL;

const PRODUCTS_BASE = [
  {
    id: "qgis",
    installerName: "installer",
  },
  {
    id: "mobile",
    name: "Mobile",
    iconUrl: `${base}img/ngmobile_logo.svg`,
    installerName: "nextgis-mobile",
  },
  {
    id: "collector",
    name: "Collector",
    iconUrl: `${base}img/ngcollector_logo.svg`,
    installerName: "nextgis-collector",
  },
  {
    id: "tracker",
    name: "Tracker",
    iconUrl: `${base}img/ngtracker_logo.svg`,
    installerName: "nextgis-tracker",
  },
];

export const extendProductsWithReleases = (
  releases: Release[],
  isRuRegion: boolean,
): SoftwareProduct[] => {
  const products = PRODUCTS_BASE.map((p) => {
    if (p.id === "qgis") {
      return {
        ...p,
        name: isRuRegion ? "NextGIS QGIS" : "QGIS",
        iconUrl: isRuRegion
          ? `${base}img/ngqgis_logo.svg`
          : `${base}img/qgis_logo.svg`,
      };
    }
    return p as typeof p & { name: string; iconUrl: string };
  });

  return products.map((product) => {
    const installers: Record<string, Installer> = {};
    let downloadPage: string | null = null;

    // Add file-based installers from server releases
    for (const release of releases) {
      if (
        product.installerName === release.name.toLowerCase() &&
        release.files.length > 0
      ) {
        const opts = Object.fromEntries(
          release.options.map(({ key, value }) => [key, value]),
        );
        if (opts.os && opts.dist) {
          installers[`${opts.os}-${opts.dist}`] = {
            external: false,
            fileId: release.files[0].id,
            fileName: release.files[0].name,
            link: release.files[0].link,
          };
        }
      }
    }

    // Apply product-specific hardcoded links / overrides
    if (product.id === "qgis") {
      if (!isRuRegion) {
        // The official download page handles every supported OS in one place.
        for (const installerKey of Object.keys(installers)) {
          delete installers[installerKey];
        }
        downloadPage = VANILLA_QGIS_DOWNLOAD_PAGE;
      } else {
        // RU: keep server installers, add Linux network link
        installers["linux-network"] = {
          external: true,
          fileId: null,
          fileName: null,
          link: NEXTGIS_QGIS_LINUX_LINK,
        };
      }
    }

    if (product.id === "mobile") {
      installers["android-network"] = {
        external: true,
        fileId: null,
        fileName: null,
        link: MOBILE_ANDROID_LINK,
      };
    }

    if (product.id === "collector") {
      installers["android-network"] = {
        external: true,
        fileId: null,
        fileName: null,
        link: COLLECTOR_ANDROID_LINK,
      };
    }

    if (product.id === "tracker") {
      installers["android-network"] = {
        external: true,
        fileId: null,
        fileName: null,
        link: TRACKER_ANDROID_LINK,
      };
    }

    return {
      id: product.id,
      name: product.name,
      iconUrl: product.iconUrl,
      downloadPage,
      installers,
    };
  });
};
